import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BookOpenCheck, Mail, ReceiptText, Wallet } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { accounts, EmailAccount, formatMoney, receipts, ReceiptListItem, ReceiptStats, ReceiptType } from "../api/client";
import { formatReceiptDate } from "../receiptDate";

const TYPE_LABEL: Record<ReceiptType, string> = {
  receipt: "Receipt",
  bill: "Bill",
  income: "Income",
};

function formatCents(cents?: number | null, currency?: string | null): string {
  if (cents == null) return "—";
  const value = cents / 100;
  try {
    return value.toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
  } catch {
    return value.toFixed(2);
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const [mailAccounts, setMailAccounts] = useState<EmailAccount[]>([]);
  const [recent, setRecent] = useState<ReceiptListItem[]>([]);
  const [summary, setSummary] = useState<ReceiptStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    Promise.allSettled([accounts.list(), receipts.list({ limit: 10 }), receipts.stats()]).then((results) => {
      setMailAccounts(results[0].status === "fulfilled" ? results[0].value : []);
      setRecent(results[1].status === "fulfilled" ? results[1].value : []);
      setSummary(results[2].status === "fulfilled" ? results[2].value : null);
      setLoadError(results.some((r) => r.status === "rejected"));
      setLoading(false);
    });
  }, []);

  const firstName = user?.full_name.split(" ")[0] || "there";

  const connectedCount = mailAccounts.filter((a) => a.is_active).length;
  const notInQbo = (summary?.by_qbo_status.none ?? 0) + (summary?.by_qbo_status.error ?? 0);
  const spend = summary?.spend_30d.map((s) => formatMoney(s.total_cents, s.currency)).join(" + ") || formatMoney(0);

  const stats = [
    { label: "Connected mailboxes", value: connectedCount, to: "/app/connections", icon: Mail },
    { label: "Receipts captured", value: summary?.total ?? 0, to: "/app/receipts", icon: ReceiptText },
    { label: "Spent (last 30 days)", value: spend, to: "/app/vendors", icon: Wallet },
    { label: "Not yet in QuickBooks", value: notInQbo, to: "/app/quickbooks", icon: BookOpenCheck },
  ];

  return (
    <div className="page dashboard-page">
      <div className="page-header">
        <h1>Good to see you, {firstName}</h1>
        <p className="page-sub">An overview of what has been captured and filed from your inboxes.</p>
      </div>
      {loadError && <div className="page-error">Some of your numbers couldn't be loaded. Refresh to try again.</div>}

      <div className="stat-row">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} className="stat-card">
            <span className="stat-icon">
              <s.icon size={16} />
            </span>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{loading ? "…" : s.value}</div>
          </Link>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Recent receipts</h2>
        <Link to="/app/receipts" style={{ fontSize: 13, fontWeight: 550 }}>View all</Link>
      </div>
      {loading ? (
        <div className="page-empty">Loading…</div>
      ) : recent.length === 0 ? (
        <div className="page-empty">
          <p>No receipts yet.</p>
          <Link to="/app/connections" className="btn btn-primary">Connect a mailbox</Link>
        </div>
      ) : (
        <div className="receipt-card-list">
          {recent.map((r) => (
            <Link key={r.id} to={`/app/receipts/${r.id}`} className="receipt-card">
              <div className="receipt-card-icon">
                <ReceiptText size={18} />
              </div>
              <div className="receipt-card-body">
                <div className="receipt-card-title">{r.vendor_name || r.sender || "Unknown vendor"}</div>
                <div className="receipt-card-sub">
                  {r.subject || "(no subject)"} · {formatReceiptDate(r)}
                </div>
              </div>
              <div className="receipt-card-amount">{formatCents(r.amount_cents, r.currency)}</div>
              {r.receipt_type && (
                <span className={`badge type-${r.receipt_type}`}>
                  {TYPE_LABEL[r.receipt_type] ?? r.receipt_type}
                </span>
              )}
              {r.qbo_sync_status === "synced" && <span className="badge matched">In QuickBooks</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
